import PropTypes from 'prop-types';
import React from 'react';
import compact from 'lodash/compact';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { bindAll } from '@bibliocommons/utils-react';
import { withRouter } from '@bibliocommons/utils-routing';
import AnalyticsConstants from '@bibliocommons/constants-analytics';
import { catalogBibShape, listShape, recommendationShape, routerShape } from '@bibliocommons/bc-prop-types';

import Context from '@bibliocommons/context';

import { selectCatalogBib } from 'app/selectors/catalogBibs/CatalogBibSelector';
import { selectLinkedDataForCurrentBib } from 'app/selectors/bibs/LinkedDataSelector';
import { selectAnalyticsContextForCurrentBib } from 'app/selectors/bibs/AnalyticsSelector';
import { selectTagSummariesForCurrentBib } from 'app/selectors/bibs/UgcSelector';
import { selectPreviewFeatures } from 'app/selectors/PreviewSelector';
import {
  selectStaffListsForCurrentBib,
  selectRecommendationsForCurrentBib,
  selectRelatedTitlesForCurrentBib,
  selectAuthorSuggestionsForCurrentBib
} from 'app/selectors/bibs/DiscoverySelector';
import {
  selectRelatedAuthorsForCurrentBib,
  selectOnlineResourcesForCurrentBib
} from 'app/selectors/bibs/EvaluationSelector';

import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import { AnalyticsContextContainer } from 'app/components/analytics/AnalyticsContext';
import NotFoundPage from 'app/components/NotFoundPage/NotFoundPage';
import { trackCatalogExplore, trackContentClick } from 'app/actions/AnalyticsGa4Actions';
import BibPage from './BibPage';

export class BibPageContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  handleRelatedTitleClick(relatedTitle) {
    const bibId = relatedTitle.get('metadataId');
    const metadata = {
      metadata_category_type: 'bibliographic_details_title',
      metadata_value: relatedTitle.getIn(['brief', 'title'])
    };
    const ui = {
      ui_container_type: 'section',
      ui_container_title: 'related-titles',
      ui_component_type: 'link-image'
    };

    this.props.trackContentClick({ bibId, metadata, ui });
  }

  renderDocumentHead() {
    const { catalogBib, linkedData } = this.props;
    const pageTitle = catalogBib.getIn(['brief', 'title']);
    const imageUrl = catalogBib.getIn(['brief', 'coverImage', 'large']);
    const authorName = catalogBib.getIn(['brief', 'creators', 0, 'fullName']);
    const description = compact([pageTitle, authorName, catalogBib.getIn(['brief', 'description'])]).join(' — ');
    const pageUrl = `${this.context.baseURL}/v2/record/${catalogBib.get('metadataId')}`;
    const noIndex = catalogBib.get('localizationLevel') === 'UNMATCHED';

    return (
      <DocumentHeadContainer
        pageTitle={pageTitle}
        description={description}
        imageUrl={imageUrl}
        linkedData={linkedData}
        pageUrl={pageUrl}
        noIndex={noIndex}
      />
    );
  }

  render() {
    const {
      catalogBib,
      staffLists,
      recommendations,
      tags,
      relatedTitles,
      authorSuggestions,
      relatedAuthors,
      analyticsContext,
      onlineResources,
      trackCatalogExplore: trackCatalogExploreAction
    } = this.props;

    if (catalogBib) {
      const displayProps = {
        staffLists,
        recommendations,
        relatedTitles,
        tags,
        authorSuggestions,
        relatedAuthors,
        onlineResources
      };

      return (
        <div>
          {this.renderDocumentHead()}
          <AnalyticsContextContainer context={analyticsContext} contextName={AnalyticsConstants.CONTEXTS.BIB} />
          <BibPage
            catalogBib={this.props.catalogBib}
            {...displayProps}
            handleRelatedTitleClick={this.handleRelatedTitleClick}
            trackCatalogExplore={trackCatalogExploreAction}
          />
        </div>
      );
    }

    return <NotFoundPage />;
  }
}

BibPageContainer.propTypes = {
  catalogBib: catalogBibShape,
  linkedData: ImmutablePropTypes.map.isRequired,
  previewFeatures: ImmutablePropTypes.map.isRequired,
  analyticsContext: ImmutablePropTypes.map.isRequired,
  router: routerShape.isRequired,

  // Props below are used to conditionally render section titles and dividers
  staffLists: ImmutablePropTypes.listOf(listShape).isRequired,
  relatedTitles: ImmutablePropTypes.listOf(catalogBibShape).isRequired,
  recommendations: ImmutablePropTypes.listOf(recommendationShape).isRequired,
  tags: ImmutablePropTypes.list.isRequired,
  authorSuggestions: ImmutablePropTypes.list.isRequired,
  relatedAuthors: ImmutablePropTypes.list.isRequired,
  onlineResources: ImmutablePropTypes.list.isRequired,
  trackCatalogExplore: PropTypes.func.isRequired,
  trackContentClick: PropTypes.func
};

BibPageContainer.contextType = Context;

const mapStateToProps = state => ({
  catalogBib: selectCatalogBib(state),
  linkedData: selectLinkedDataForCurrentBib(state),
  analyticsContext: selectAnalyticsContextForCurrentBib(state),
  previewFeatures: selectPreviewFeatures(state),

  // Props below are used to conditionally render section titles and dividers
  staffLists: selectStaffListsForCurrentBib(state),
  relatedTitles: selectRelatedTitlesForCurrentBib(state),
  recommendations: selectRecommendationsForCurrentBib(state),
  tags: selectTagSummariesForCurrentBib(state),
  authorSuggestions: selectAuthorSuggestionsForCurrentBib(state),
  relatedAuthors: selectRelatedAuthorsForCurrentBib(state),
  onlineResources: selectOnlineResourcesForCurrentBib(state)
});

const mapDispatchToProps = {
  trackCatalogExplore,
  trackContentClick
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(BibPageContainer)));
